// Capital Readiness Score (CRS) types - Phase 8

export type CRSTier = 'investment_ready' | 'near_ready' | 'developing' | 'early_stage' | 'not_ready';

export type GapSeverity = 'critical' | 'high' | 'medium' | 'low';

export type CRSDimension =
  | 'financial_preparedness'
  | 'market_validation'
  | 'team_strength'
  | 'legal_compliance'
  | 'pitch_quality'
  | 'traction_metrics'
  | 'investor_readiness';

export const CRS_DIMENSIONS: CRSDimension[] = [
  'financial_preparedness',
  'market_validation',
  'team_strength',
  'legal_compliance',
  'pitch_quality',
  'traction_metrics',
  'investor_readiness',
];

export interface DimensionScores {
  financial_preparedness?: number;
  market_validation?: number;
  team_strength?: number;
  legal_compliance?: number;
  pitch_quality?: number;
  traction_metrics?: number;
  investor_readiness?: number;
  [k: string]: number | undefined;
}

export interface CRSGap {
  dimension: CRSDimension | string;
  severity: GapSeverity;
  description: string;
}

export interface RecommendedProvider {
  id?: number;
  name: string;
  provider_type?: string;
  website_url?: string | null;
  contact_email?: string | null;
}

export interface ImprovementStep {
  step: string;
  priority?: number;
  dimension?: CRSDimension | string;
  recommended_providers?: RecommendedProvider[];
}

export interface CapitalReadinessScore {
  id: number;
  startup_id?: number | null;
  project_id?: number | null;
  overall_score: number;
  tier: CRSTier;
  dimension_scores: DimensionScores;
  gaps: CRSGap[];
  improvement_roadmap: ImprovementStep[];
  version?: number;
  created_at: string;
  updated_at?: string;

  // Optional summaries
  startup?: { id: number; name: string } | null;
  project?: { id: number; project_name: string } | null;
}

export interface CRSListFilters {
  startup_id?: number;
  project_id?: number;
  min_score?: number;
  tier?: CRSTier;
  page?: number;
  size?: number;
}

export interface CRSListResponse {
  scores: CapitalReadinessScore[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface CRSLeaderboardEntry {
  id: number;
  startup_id?: number | null;
  project_id?: number | null;
  entity_name: string;
  entity_kind: 'startup' | 'project';
  overall_score: number;
  tier: CRSTier;
  gap_count: number;
  created_at?: string;
}

export interface ScoreRequest {
  startup_id?: number;
  project_id?: number;
}

export type ProviderType =
  | 'accountant'
  | 'lawyer'
  | 'pitch_coach'
  | 'financial_advisor'
  | 'cap_table'
  | 'marketing'
  | 'recruiter'
  | 'consultant'
  | string;

export interface ServiceProvider {
  id: number;
  name: string;
  provider_type: ProviderType;
  specialties?: string[];
  contact_email?: string | null;
  website_url?: string | null;
  description?: string | null;
  active: boolean;
  created_at?: string;
}

export interface ProviderListFilters {
  provider_type?: ProviderType;
  specialty?: string;
}

export interface CreateProviderRequest {
  name: string;
  provider_type: ProviderType;
  specialties?: string[];
  contact_email?: string | null;
  website_url?: string | null;
  description?: string | null;
  active?: boolean;
}
